/**
 * Global HR — Sprint 2 · Employee Self-Service portal.
 * Route: /hr/me  (any authenticated staff)
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, User, Calendar, Wallet, Receipt, FileText, Coins, ArrowLeft, LogOut, Plus, Home } from 'lucide-react';
import { apiClient, API } from '../services/api';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const Card = ({ children, className = '' }) => (
  <div className={`bg-white border border-black/5 rounded-2xl ${className}`}>{children}</div>
);
const Btn = ({ variant = 'primary', className = '', ...rest }) => {
  const styles = {
    primary: 'bg-[#0f5c3d] text-white hover:bg-[#0b4a30]',
    ghost: 'border border-[#0f5c3d]/20 text-[#0f5c3d] hover:bg-[#f7fbf8]',
  }[variant];
  return <button {...rest} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${styles} ${className}`} />;
};
const Input = (p) => <input {...p} className={`w-full px-3 py-2 border border-slate-300 rounded text-sm ${p.className || ''}`} />;
const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 mb-1 font-semibold">{label}</label>
    {children}
  </div>
);

const TABS = [
  { key: 'overview', label: 'Overview', icon: Home },
  { key: 'profile', label: 'My profile', icon: User },
  { key: 'payslips', label: 'Payslips', icon: Wallet },
  { key: 'leaves', label: 'Leaves', icon: Calendar },
  { key: 'attendance', label: 'Attendance', icon: Calendar },
  { key: 'reimbursements', label: 'Claims', icon: Receipt },
  { key: 'loans', label: 'Loans', icon: Coins },
  { key: 'documents', label: 'Documents', icon: FileText },
];

const SelfServicePage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [me, setMe] = useState(null);
  useEffect(() => {
    apiClient.get('/hr/self/me').then((r) => setMe(r.data)).catch(() => setMe({}));
  }, []);
  const handleLogout = async () => { await logout(); navigate('/login'); };
  if (!me) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#0f5c3d]" /></div>;
  return (
    <div className="min-h-screen bg-[#f5f8f6]">
      {/* Header */}
      <div className="bg-[#0b1a2e] text-white">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="p-1.5 hover:bg-white/10 rounded" title="Admin"><ArrowLeft className="w-4 h-4" /></Link>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#7bbf95] font-semibold">My Workplace</div>
              <div className="text-lg font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Welcome, {user?.name || 'there'}</div>
            </div>
          </div>
          <button onClick={handleLogout} data-testid="ess-logout" className="p-2 hover:bg-white/10 rounded" title="Log out"><LogOut className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex overflow-x-auto gap-1 mb-4 bg-white p-1 rounded-full border border-black/5">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`ess-tab-${t.key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap ${
                tab === t.key ? 'bg-[#0f5c3d] text-white' : 'text-[#0b1a2e]/60 hover:bg-[#f7fbf8]'
              }`}
            >
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {tab === 'overview' && <OverviewTab me={me} />}
        {tab === 'profile' && <ProfileTab me={me} onSaved={setMe} />}
        {tab === 'payslips' && <PayslipsTab />}
        {tab === 'leaves' && <LeavesTab />}
        {tab === 'attendance' && <AttendanceTab />}
        {tab === 'reimbursements' && <ReimbursementsTab />}
        {tab === 'loans' && <LoansTab />}
        {tab === 'documents' && <DocumentsTab userId={me.user_id} />}
      </div>
    </div>
  );
};

const OverviewTab = ({ me }) => {
  const [dash, setDash] = useState(null);
  useEffect(() => { apiClient.get('/hr/self/dashboard').then((r) => setDash(r.data)).catch(() => setDash({})); }, []);
  if (!dash) return <Loader2 className="w-5 h-5 inline animate-spin" />;
  const tiles = [
    { label: 'Pending leaves', value: dash.pending_leaves, icon: Calendar, color: 'text-amber-700 bg-amber-50 border-amber-200' },
    { label: 'Approved leaves', value: dash.approved_leaves, icon: Calendar, color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
    { label: 'Open loans', value: dash.open_loans, icon: Coins, color: 'text-indigo-700 bg-indigo-50 border-indigo-200' },
    { label: 'Pending claims', value: dash.pending_reimbursements, icon: Receipt, color: 'text-purple-700 bg-purple-50 border-purple-200' },
    { label: 'Documents on file', value: dash.documents_count, icon: FileText, color: 'text-sky-700 bg-sky-50 border-sky-200' },
  ];
  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="text-xs uppercase tracking-widest text-[#0f5c3d] font-semibold">You are</div>
        <div className="mt-1 text-2xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{me.name}</div>
        <div className="text-xs text-[#0b1a2e]/60 mt-1">{[me.employee_code, me.designation, me.country].filter(Boolean).join(' · ') || 'Complete your profile below'}</div>
      </Card>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {tiles.map((t) => (
          <Card key={t.label} className={`p-3 border ${t.color}`} data-testid={`ess-tile-${t.label.replace(/\s/g,'-').toLowerCase()}`}>
            <t.icon className="w-4 h-4 mb-1" />
            <div className="text-2xl font-bold">{t.value ?? 0}</div>
            <div className="text-[10px] uppercase tracking-widest font-semibold">{t.label}</div>
          </Card>
        ))}
      </div>
      {dash.latest_payslip && (
        <Card className="p-5">
          <div className="text-sm font-semibold text-[#0b1a2e]">Latest payslip · {dash.latest_payslip.period}</div>
          <div className="text-xs text-[#0b1a2e]/60 mt-1">Net: <b>{dash.latest_payslip.net_pay || dash.latest_payslip.net || '—'}</b> {dash.latest_payslip.currency || ''}</div>
        </Card>
      )}
    </div>
  );
};

const ProfileTab = ({ me, onSaved }) => {
  const [f, setF] = useState(me);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const payload = { address: f.address, emergency_contact: f.emergency_contact, phone: f.phone,
        bank_account_name: f.bank_account_name, bank_account_number: f.bank_account_number,
        bank_ifsc_or_sort: f.bank_ifsc_or_sort, bank_name: f.bank_name };
      await apiClient.patch('/hr/self/me', payload);
      toast({ title: 'Profile saved' });
      onSaved({ ...me, ...payload });
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
    finally { setSaving(false); }
  };
  return (
    <Card className="p-5">
      <div className="text-xs uppercase tracking-widest text-[#0f5c3d] font-semibold mb-3">My profile</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Full name"><Input value={f.name || ''} disabled className="bg-slate-50" /></Field>
        <Field label="Email"><Input value={f.email || ''} disabled className="bg-slate-50" /></Field>
        <Field label="Employee code"><Input value={f.employee_code || ''} disabled className="bg-slate-50" /></Field>
        <Field label="Country"><Input value={f.country || ''} disabled className="bg-slate-50" /></Field>
        <Field label="Phone"><Input value={f.phone || ''} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="prof-phone" /></Field>
        <Field label="Emergency contact"><Input value={f.emergency_contact || ''} onChange={(e) => setF({ ...f, emergency_contact: e.target.value })} data-testid="prof-emergency" /></Field>
        <div className="sm:col-span-2"><Field label="Address"><Input value={f.address || ''} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="prof-address" /></Field></div>
        <Field label="Bank name"><Input value={f.bank_name || ''} onChange={(e) => setF({ ...f, bank_name: e.target.value })} /></Field>
        <Field label="Bank account #"><Input value={f.bank_account_number || ''} onChange={(e) => setF({ ...f, bank_account_number: e.target.value })} /></Field>
        <Field label="IFSC / Sort"><Input value={f.bank_ifsc_or_sort || ''} onChange={(e) => setF({ ...f, bank_ifsc_or_sort: e.target.value })} /></Field>
      </div>
      <div className="mt-4 flex justify-end">
        <Btn onClick={save} disabled={saving} data-testid="prof-save-btn">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Save</Btn>
      </div>
    </Card>
  );
};

const PayslipsTab = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { apiClient.get('/hr/self/payslips').then((r) => setItems(r.data.data || [])).catch(() => setItems([])); }, []);
  return (
    <Card>
      <div className="p-3 border-b border-black/5 text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/60">My payslips</div>
      {items.length === 0 ? <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No payslips yet.</div> : (
        <table className="w-full text-sm">
          <thead className="bg-[#f7fbf8]"><tr><th className="text-left p-2 text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">Period</th><th className="text-right p-2 text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">Gross</th><th className="text-right p-2 text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">Net</th><th></th></tr></thead>
          <tbody>{items.map((p) => (
            <tr key={p.id || p.period} className="border-t border-black/5">
              <td className="p-2">{p.period}</td>
              <td className="p-2 text-right">{p.gross || '-'}</td>
              <td className="p-2 text-right font-semibold">{p.net_pay || p.net}</td>
              <td className="p-2 text-right"><a href={`${API}/books/payslip/${p.id}.pdf`} target="_blank" rel="noreferrer" className="text-[#0f5c3d] text-xs">View</a></td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </Card>
  );
};

const LeavesTab = () => {
  const [data, setData] = useState({ data: [], balances: [] });
  const [types, setTypes] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const load = () => Promise.all([
    apiClient.get('/hr/self/leaves'),
    apiClient.get('/hr/leave-types'),
  ]).then(([l, t]) => { setData(l.data); setTypes(t.data.data || []); });
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {data.balances.map((b) => (
          <Card key={b.leave_type_id} className="p-3">
            <div className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 font-semibold">{b.name}</div>
            <div className="text-lg font-bold text-[#0f5c3d]">{b.balance} <span className="text-xs text-[#0b1a2e]/50 font-normal">/ {b.days_per_year}</span></div>
            <div className="text-[10px] text-[#0b1a2e]/50">used {b.used}</div>
          </Card>
        ))}
      </div>
      <Card>
        <div className="p-3 border-b border-black/5 flex justify-between">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/60">My leave requests</div>
          <Btn onClick={() => setShowNew(true)} data-testid="new-leave-btn"><Plus className="w-3.5 h-3.5" /> Request leave</Btn>
        </div>
        {data.data.length === 0 ? <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No leave requests.</div> : (
          <table className="w-full text-sm">
            <thead className="bg-[#f7fbf8]"><tr><th className="text-left p-2 text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">Type</th><th className="text-left p-2 text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">From</th><th className="text-left p-2 text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">To</th><th className="text-right p-2 text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">Days</th><th className="text-left p-2 text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">Status</th></tr></thead>
            <tbody>{data.data.map((l) => {
              const t = types.find((tt) => tt.id === l.leave_type_id);
              return (
                <tr key={l.id} className="border-t border-black/5">
                  <td className="p-2">{t?.name || '—'}</td>
                  <td className="p-2 text-xs">{l.from_date}</td>
                  <td className="p-2 text-xs">{l.to_date}</td>
                  <td className="p-2 text-right">{l.days}</td>
                  <td className="p-2"><span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${l.status === 'pending' ? 'bg-amber-100 text-amber-800' : l.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>{l.status}</span></td>
                </tr>
              );
            })}</tbody>
          </table>
        )}
      </Card>
      {showNew && <NewLeaveModal types={types} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
};

const NewLeaveModal = ({ types, onClose, onSaved }) => {
  const [f, setF] = useState({ leave_type_id: '', from_date: '', to_date: '', reason: '' });
  const submit = async () => {
    try { await apiClient.post('/hr/leave-requests', f); toast({ title: 'Submitted' }); onSaved(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl p-5">
        <div className="text-sm font-semibold mb-3">New leave request</div>
        <div className="space-y-3">
          <Field label="Type"><select value={f.leave_type_id} onChange={(e) => setF({ ...f, leave_type_id: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded text-sm bg-white" data-testid="ess-leave-type"><option value="">Select</option>{types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></Field>
          <Field label="From"><Input type="date" value={f.from_date} onChange={(e) => setF({ ...f, from_date: e.target.value })} data-testid="ess-leave-from" /></Field>
          <Field label="To"><Input type="date" value={f.to_date} onChange={(e) => setF({ ...f, to_date: e.target.value })} data-testid="ess-leave-to" /></Field>
          <Field label="Reason"><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="ess-leave-reason" /></Field>
          <div className="flex justify-end gap-2 pt-2">
            <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn onClick={submit} data-testid="ess-leave-submit">Submit</Btn>
          </div>
        </div>
      </div>
    </div>
  );
};

const AttendanceTab = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { apiClient.get('/hr/self/attendance').then((r) => setItems(r.data.data || [])).catch(() => setItems([])); }, []);
  return (
    <Card>
      <div className="p-3 border-b border-black/5 text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/60">Attendance summary</div>
      {items.length === 0 ? <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No attendance records yet.</div> :
        <table className="w-full text-sm"><thead className="bg-[#f7fbf8]"><tr><th className="text-left p-2 text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">Period</th><th className="text-right p-2 text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">Present</th><th className="text-right p-2 text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">Absent</th><th className="text-right p-2 text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">LWP</th></tr></thead>
        <tbody>{items.map((r) => (<tr key={r.id || r.period} className="border-t border-black/5"><td className="p-2">{r.period}</td><td className="p-2 text-right">{r.present || 0}</td><td className="p-2 text-right">{r.absent || 0}</td><td className="p-2 text-right">{r.lwp || 0}</td></tr>))}</tbody></table>
      }
    </Card>
  );
};

const ReimbursementsTab = () => {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const load = () => Promise.all([
    apiClient.get('/hr/self/reimbursements'),
    apiClient.get('/hr/masters/reimb-categories').catch(() => ({ data: { data: [] } })),
  ]).then(([a, b]) => { setItems(a.data.data || []); setCats(b.data.data || []); });
  useEffect(() => { load(); }, []);
  return (
    <Card>
      <div className="p-3 border-b border-black/5 flex justify-between">
        <div className="text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/60">My reimbursement claims</div>
        <Btn onClick={() => setShowNew(true)} data-testid="ess-new-reimb-btn"><Plus className="w-3.5 h-3.5" /> Claim</Btn>
      </div>
      {items.length === 0 ? <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No claims yet.</div> :
        <table className="w-full text-sm"><thead className="bg-[#f7fbf8]"><tr><th className="text-left p-2 text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">Date</th><th className="text-left p-2 text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">Category</th><th className="text-right p-2 text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">Amount</th><th className="text-left p-2 text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">Status</th></tr></thead>
        <tbody>{items.map((r) => { const c = cats.find((cc) => cc.id === r.category_id); return (
          <tr key={r.id} className="border-t border-black/5"><td className="p-2 text-xs">{r.date}</td><td className="p-2">{c?.name || '-'}</td><td className="p-2 text-right">{r.amount}</td><td className="p-2"><span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${r.status === 'pending' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>{r.status}</span></td></tr>
        );})}</tbody></table>
      }
      {showNew && <NewReimbModal cats={cats} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </Card>
  );
};

const NewReimbModal = ({ cats, onClose, onSaved }) => {
  const [f, setF] = useState({ category_id: '', amount: '', date: new Date().toISOString().slice(0, 10), description: '' });
  const submit = async () => {
    try { await apiClient.post('/hr/self/reimbursements', { ...f, amount: parseFloat(f.amount || 0) }); toast({ title: 'Submitted' }); onSaved(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl p-5">
        <div className="text-sm font-semibold mb-3">New claim</div>
        <div className="space-y-3">
          <Field label="Category"><select value={f.category_id} onChange={(e) => setF({ ...f, category_id: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded text-sm bg-white"><option value="">Select</option>{cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
          <Field label="Date"><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
          <Field label="Amount"><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></Field>
          <Field label="Description"><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
          <div className="flex justify-end gap-2 pt-2">
            <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn onClick={submit} data-testid="ess-reimb-submit">Submit</Btn>
          </div>
        </div>
      </div>
    </div>
  );
};

const LoansTab = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { apiClient.get('/hr/self/loans').then((r) => setItems(r.data.data || [])).catch(() => setItems([])); }, []);
  return (
    <Card>
      <div className="p-3 border-b border-black/5 text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/60">My loans</div>
      {items.length === 0 ? <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No loans on file.</div> :
        <table className="w-full text-sm"><thead className="bg-[#f7fbf8]"><tr><th className="text-left p-2 text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">Type</th><th className="text-right p-2 text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">Principal</th><th className="text-right p-2 text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">EMIs paid</th><th className="text-right p-2 text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">Outstanding</th><th className="text-left p-2 text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">Status</th></tr></thead>
          <tbody>{items.map((l) => (<tr key={l.id} className="border-t border-black/5"><td className="p-2">{l.loan_type_code || l.loan_type || '-'}</td><td className="p-2 text-right">{l.principal}</td><td className="p-2 text-right">{l.installments_paid || 0}/{l.tenure_months || l.emi_count}</td><td className="p-2 text-right">{l.outstanding}</td><td className="p-2 text-xs">{l.status}</td></tr>))}</tbody></table>
      }
    </Card>
  );
};

const DocumentsTab = ({ userId }) => {
  const [grouped, setGrouped] = useState({});
  const [folders, setFolders] = useState([]);
  useEffect(() => { apiClient.get(`/hr/employees/${userId}/documents`).then((r) => { setGrouped(r.data.grouped || {}); setFolders(r.data.folders || []); }).catch(() => {}); }, [userId]);
  return (
    <div className="space-y-3">
      {folders.map((f) => (
        <Card key={f}>
          <div className="p-3 border-b border-black/5 text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/60 flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#0f5c3d]" /> {f} <span className="text-[10px] text-[#0b1a2e]/40">({(grouped[f] || []).length})</span>
          </div>
          {(grouped[f] || []).length === 0 ? <div className="p-4 text-xs text-[#0b1a2e]/40">Empty.</div> : (
            <table className="w-full text-sm"><tbody>{(grouped[f] || []).map((d) => (
              <tr key={d.id} className="border-t border-black/5">
                <td className="p-2 text-sm">{d.title}</td>
                <td className="p-2 text-xs text-[#0b1a2e]/50">{d.filename}</td>
                <td className="p-2 text-right"><a href={`${API}${d.url}`} target="_blank" rel="noreferrer" className="text-[#0f5c3d] text-xs">View</a></td>
              </tr>
            ))}</tbody></table>
          )}
        </Card>
      ))}
    </div>
  );
};

export default SelfServicePage;
